import type { JdAfterSalesFacts } from './jdOrders'
import { internalProductCostOn } from './productMaster'
import type { InternalProductInfoRecord, ProductMasterRecord, ProductRecord } from './types'

/**
 * 京东售后损失与扣售后利润（D-211，2026-08-22 Gavin 修正口径）：
 *   扣售后利润 = 含返点利润 − 售后损失
 *   售后损失 = 往返运费 + 纸箱报废 + 产品货损，只对「退款成功且申请时已发货」（二退）计：
 *     往返运费 = 京东快递运费 × 二退服务单数 × 2（发出与退回两笔运费都损失）
 *     纸箱报废 = 包装成本 × 二退服务单数
 *     产品货损 = 单件产品成本 × 二退件数 × 5%（退回产品按 5% 折损）
 * 一退（未发货退款）货未出门，不产生损失；退款金额本身不从利润扣除
 * （对应销售的成本与收入视作未发生，商智口径下作参考展示）。
 * 缺成本或未拆分的二退件数不按 0 伪造货损，计数披露。
 */
export const JD_AFTER_SALES_DAMAGE_RATE = 0.05
export const JD_RETURN_FREIGHT_LEGS = 2

interface CostDefaultsLike {
  jdShippingFee: number
  packagingCost: number
}

export interface JdAfterSalesLoss {
  requestedDays: number
  coveredDays: number
  /** 窗口每一天都在售后数据覆盖范围内 */
  complete: boolean
  /** 参考值：窗口内退款成功金额（按申请日），不入利润公式 */
  refundAmount: number
  refundItemCount: number
  secondReturnOrderCount: number
  secondReturnItemCount: number
  freightLoss: number
  packagingLoss: number
  damageLoss: number
  coveredDamageItems: number
  uncoveredDamageItems: number
  totalLoss: number
  missingCostSkuIds: string[]
}

export function summarizeJdAfterSalesLoss(
  facts: JdAfterSalesFacts | null,
  dates: string[],
  products: ProductRecord[],
  productMasterRecords: Map<string, ProductMasterRecord>,
  internalRecordsByCode: Map<string, InternalProductInfoRecord[]>,
  costDefaults: CostDefaultsLike,
): JdAfterSalesLoss {
  const empty: JdAfterSalesLoss = {
    requestedDays: dates.length,
    coveredDays: 0,
    complete: false,
    refundAmount: 0,
    refundItemCount: 0,
    secondReturnOrderCount: 0,
    secondReturnItemCount: 0,
    freightLoss: 0,
    packagingLoss: 0,
    damageLoss: 0,
    coveredDamageItems: 0,
    uncoveredDamageItems: 0,
    totalLoss: 0,
    missingCostSkuIds: [],
  }
  if (!facts || !dates.length) return empty
  const coveredDays = dates.filter(
    (date) => date >= facts.meta.queryStart && date <= facts.meta.queryEnd,
  ).length
  const dateSet = new Set(dates)
  let refundAmount = 0
  let refundItemCount = 0
  let secondReturnOrderCount = 0
  let secondReturnItemCount = 0
  let uncoveredDamageItems = 0
  for (const row of facts.daily) {
    if (!dateSet.has(row.date)) continue
    refundAmount += row.successfulRefundAmount
    refundItemCount += row.successfulItemCount
    secondReturnOrderCount += row.successfulShippedCount
    secondReturnItemCount += row.successfulShippedItemCount
    uncoveredDamageItems += row.unattributedSuccessfulShippedItemCount || 0
  }
  const productByJdSkuId = new Map(
    products.filter((product) => product.level === 'sku').map((product) => [product.jdSkuId || product.id, product]),
  )
  let damageLoss = 0
  let coveredDamageItems = 0
  const missingCostSkuIds = new Set<string>()
  for (const row of facts.dailySku) {
    if (!dateSet.has(row.date) || !row.successfulShippedItemCount) continue
    const product = productByJdSkuId.get(row.jdSkuId)
    const merchantSku = product?.merchantSku?.trim() || ''
    const records = merchantSku ? internalRecordsByCode.get(merchantSku) : undefined
    const cost = records?.length
      ? internalProductCostOn(records, productMasterRecords.get(row.jdSkuId), row.date).productCost
      : null
    if (cost === null || cost === undefined) {
      uncoveredDamageItems += row.successfulShippedItemCount
      missingCostSkuIds.add(row.jdSkuId)
      continue
    }
    damageLoss += cost * row.successfulShippedItemCount * JD_AFTER_SALES_DAMAGE_RATE
    coveredDamageItems += row.successfulShippedItemCount
  }
  const freightLoss = costDefaults.jdShippingFee * JD_RETURN_FREIGHT_LEGS * secondReturnOrderCount
  const packagingLoss = costDefaults.packagingCost * secondReturnOrderCount
  return {
    requestedDays: dates.length,
    coveredDays,
    complete: coveredDays === dates.length,
    refundAmount,
    refundItemCount,
    secondReturnOrderCount,
    secondReturnItemCount,
    freightLoss,
    packagingLoss,
    damageLoss,
    coveredDamageItems,
    uncoveredDamageItems,
    totalLoss: freightLoss + packagingLoss + damageLoss,
    missingCostSkuIds: [...missingCostSkuIds],
  }
}

/** 扣售后利润＝含返点利润−售后损失；售后数据未完整覆盖窗口或利润缺失时为 null，不补零 */
export function jdProfitAfterAfterSales(
  profitWithRebate: number | null,
  loss: JdAfterSalesLoss,
): number | null {
  if (profitWithRebate === null || !loss.complete) return null
  return profitWithRebate - loss.totalLoss
}
